import { ServiceChoices, ServicePriceSummary } from '../../components/proposals/ServiceChoices';
import { defaultSelections, type ServiceSelections, type ServiceQuote } from '../../shared/proposal-services';
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import SignaturePad from "signature_pad";
import DOMPurify from "dompurify";
import { proposalsApi } from "../../lib/proposalsApi";
import { api } from "../../lib/api";
import { isCalendarDatePast } from "../../lib/calendarDate";
import {
  hasDistinctTierLabel,
  logoFor,
  money,
  packagePrice,
  requiredFeeCents,
  paymentSchedule,
  requiredProducts,
  themeStyle,
  tierDiscount,
  totalCents,
  workflowSteps,
} from "../../lib/proposalPresentation";
import type { PublicProposal, ProposalTier } from "../../types/proposals";
import type {
  ProposalDocumentStatus,
  ProposalSection,
} from "../../types/proposalPresentation";
import LegacyProposalView from "./LegacyProposalView";
import { AnnotatedPhoto } from "../../components/photos/AnnotatedPhoto";
import "./proposalExperience.css";

function PackageDiscount({ proposal, tier }: { proposal: PublicProposal; tier: ProposalTier }) {
  const discount = tierDiscount(proposal, tier);
  if (!discount) return null;
  return (
    <div className="px-discount">
      <s aria-label="Original package price">{money(discount.original + requiredFeeCents(proposal) / 100)}</s>
      <span className="px-savings">Save {money(discount.savings)}</span>
    </div>
  );
}

function Dialog({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const dialog = ref.current;
    dialog?.showModal();
    return () => {
      dialog?.close();
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  return (
    <dialog
      className="px-dialog"
      ref={ref}
      onCancel={onClose}
      aria-label={title}
    >
      <header>
        <h2>{title}</h2>
        <button className="px-icon" onClick={onClose} aria-label="Close dialog">
          ×
        </button>
      </header>
      <div className="px-dialog-body">{children}</div>
    </dialog>
  );
}

function Signature({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const pad = useRef<SignaturePad | null>(null);
  const initial = useRef(value);
  useEffect(() => {
    const canvas = ref.current!;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = Math.max(300, canvas.clientWidth) * ratio;
    canvas.height = 130 * ratio;
    canvas.getContext("2d")?.scale(ratio, ratio);
    const instance = new SignaturePad(canvas, {
      backgroundColor: "#fff",
      penColor: "#17151d",
    });
    pad.current = instance;
    if (initial.current) void instance.fromDataURL(initial.current);
    const end = () => onChange(instance.isEmpty() ? "" : instance.toDataURL());
    instance.addEventListener("endStroke", end);
    return () => {
      instance.removeEventListener("endStroke", end);
      instance.off();
    };
  }, [onChange]);
  return (
    <div className="px-signature">
      <canvas ref={ref} aria-label="Draw your signature" />
      <button
        type="button"
        className="px-link"
        onClick={() => {
          pad.current?.clear();
          onChange("");
        }}
      >
        Clear signature
      </button>
    </div>
  );
}

function TierScope({ tier }: { tier: ProposalTier }) {
  return (
    <article className="px-scope">
      {hasDistinctTierLabel(tier) && (
        <p className="px-eyebrow">{tier.tier_type_label}</p>
      )}
      <h3>{tier.name}</h3>
      <p>{tier.description}</p>
      <ul>
        {tier.line_items.map((item) => (
          <li key={item.id}>
            {item.description}
            <span className="px-muted">
              {" "}
              · {item.quantity} {item.unit}
            </span>
          </li>
        ))}
      </ul>
    </article>
  );
}

export function Measurements({ proposal }: { proposal: PublicProposal }) {
  const native = proposal.measurements?.native;
  const summary = proposal.measurements?.summary;
  const area = summary?.total_area_sqft ?? native?.total_area_sqft;
  const number = (value: number | string) => Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <>
      {proposal.opportunity_address && <p className="px-measurement-address">{proposal.opportunity_address}</p>}
      <div className="px-metrics px-measurement-metrics">
        {proposal.show_total_squares && summary && (
          <div>
            <span>Total squares</span>
            <strong>{number(summary.total_squares)}</strong>
            <span>Measured roof</span>
          </div>
        )}
        {proposal.show_total_squares && summary?.squares_with_waste != null && (
          <div className="px-measurement-waste">
            <span>Squares with waste</span>
            <strong>{number(summary.squares_with_waste)}</strong>
            <span>Includes {summary.waste_percent}% waste</span>
          </div>
        )}
        {proposal.show_total_squares && proposal.total_squares != null && (summary?.squares_with_waste == null || Number(proposal.total_squares) !== summary.squares_with_waste) && (
          <div className="px-measurement-quantity">
            <span>Proposal quantity</span>
            <strong>{number(proposal.total_squares)}</strong>
            <span>Roofing squares</span>
          </div>
        )}
      </div>
      {proposal.show_total_squares && summary?.squares_with_waste != null && <p className="px-muted px-measurement-note">Squares with waste are rounded up to full bundles. Proposal quantity may include adjustments for this project.</p>}
      <div className="px-measurement-facts">
        {proposal.show_total_squares && area != null && <span>Roof area <strong>{number(area)} sq ft</strong></span>}
        {summary?.predominant_pitch && <span>Pitch <strong>{summary.predominant_pitch}</strong></span>}
        {!!native?.total_sections && <span>Roof sections <strong>{native.total_sections}</strong></span>}
      </div>
      {native?.static_map_url && (
        <figure className="px-measurement-map"><img
          className="px-diagram"
          src={native.static_map_url}
          alt="Roof measurement diagram"
        /><figcaption>Roof measurement diagram</figcaption></figure>
      )}
      {!!native?.linear_totals.length && (
        <div className="px-table-wrap px-measurement-lines">
          <table>
            <thead>
              <tr>
                <th>Roof feature</th>
                <th>Length</th>
              </tr>
            </thead>
            <tbody>
              {native.linear_totals.map((item) => (
                <tr key={item.type}>
                  <td>{item.label}</td>
                  <td>{item.total_ft} ft</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {proposal.measurements?.pdfs.map((pdf) => (
        <a
          className="px-resource"
          href={pdf.file_url}
          target="_blank"
          rel="noreferrer"
          key={pdf.id}
        >
          {pdf.original_filename || "View measurement report"} <span>↗</span>
        </a>
      ))}
      {!native && !summary && !proposal.measurements?.pdfs.length && (
        <p className="px-muted">No measurement report is included.</p>
      )}
    </>
  );
}

export default function ProposalView() {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const isPreview = params.get("preview") === "1";
  const embedded = params.get("embedded") === "1" && isPreview;
  const [embeddedProposal, setEmbeddedProposal] =
    useState<PublicProposal | null>(null);
  useEffect(() => {
    if (!embedded) return;
    const receive = (event: MessageEvent) => {
      if (
        event.origin === window.location.origin &&
        event.source === window.parent &&
        event.data?.type === "APEXOS_PROPOSAL_PREVIEW"
      )
        setEmbeddedProposal(event.data.proposal);
    };
    window.addEventListener("message", receive);
    window.parent.postMessage(
      { type: "APEXOS_PROPOSAL_PREVIEW_READY" },
      window.location.origin,
    );
    return () => window.removeEventListener("message", receive);
  }, [embedded]);
  const query = useQuery({
    queryKey: ["public-proposal", token, isPreview],
    queryFn: () => proposalsApi.getPublic(token!, { preview: isPreview }),
    enabled: !!token && !embedded,
    refetchInterval: (query) =>
      ["pending", "processing"].includes(
        query.state.data?.acceptance_workflow?.state || "",
      )
        ? 5000
        : false,
  });
  const proposal = embedded ? embeddedProposal : query.data;
  if (query.isError)
    return (
      <div className="px-message">
        <h1>Unable to open this proposal</h1>
        <p>The link may be unavailable. Please contact your representative.</p>
        <button onClick={() => query.refetch()}>Try again</button>
      </div>
    );
  if (!proposal)
    return (
      <div className="px-message" role="status">
        Loading your proposal…
      </div>
    );
  if (
    !proposal.presentation ||
    proposal.presentation_options?.enabled === false
  )
    return <LegacyProposalView />;
  return (
    <Experience
      key={token}
      proposal={proposal}
      token={token!}
      isPreview={isPreview}
      refresh={() => query.refetch()}
    />
  );
}

function Experience({
  proposal,
  token,
  isPreview,
  refresh,
}: {
  proposal: PublicProposal;
  token: string;
  isPreview: boolean;
  refresh: () => unknown;
}) {
  const options = proposal.presentation_options || {};
  const coverImage = proposal.cover_image || proposal.street_view_url;
  const brand = proposal.presentation!.brand;
  const org = proposal.organization_details;
  const [theme, setTheme] = useState<"light" | "dark">(
    options.theme || "light",
  );
  const [tierId, setTierId] = useState<number | null>(null);
  const [upgrades, setUpgrades] = useState<number[]>([]);
  const [choices, setChoices] = useState<Record<string, number>>({});
  const [stepId, setStepId] = useState<string | null>(null);
  const [comparison, setComparison] = useState(false);
  const [photoIndex, setPhotoIndex] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [comments, setComments] = useState("");
  const [signatureMode, setSignatureMode] = useState<"draw" | "type">("draw");
  const [signature, setSignature] = useState("");
  const [consent, setConsent] = useState(false);
  const [accepted, setAccepted] = useState(false);
  useEffect(() => {
    setConsent(false);
    setSignature("");
  }, [proposal.presentation?.source_version]);
  const heading = useRef<HTMLHeadingElement>(null);
  const steps = useMemo(
    () => workflowSteps(proposal, tierId),
    [proposal, tierId],
  );
  const index = Math.max(
    0,
    steps.findIndex((step) => step.id === stepId),
  );
  const step = steps[index];
  const tier = proposal.tiers.find((t) => t.id === tierId);
  const configuration = proposal.service_configuration?.version === 2 ? proposal.service_configuration : undefined;
  const [serviceSelections, setServiceSelections] = useState<ServiceSelections>(() => configuration ? defaultSelections(configuration) : {});
  useEffect(() => { setServiceSelections(configuration ? defaultSelections(configuration) : {}); }, [proposal.presentation?.source_version]);
  const serviceQuote = useQuery({
    queryKey: ['proposal-service-quote', token, proposal.presentation?.source_version, tierId, upgrades, serviceSelections],
    enabled: !!configuration,
    retry: false,
    queryFn: async () => (await api.post<ServiceQuote>(`/api/proposals/public/${token}/service-quote/`, {
      source_version: proposal.presentation?.source_version,
      tier_id: configuration?.include_roofing ? tierId ?? proposal.tiers[0]?.id ?? null : null,
      selected_upgrades: upgrades, service_selections: serviceSelections,
    })).data,
  });
  const priceReady = configuration ? !!serviceQuote.data && !serviceQuote.isFetching : !!tier;
  const packageReady = configuration ? (!configuration.include_roofing || !!tier) && priceReady : !!tier;
  const total = configuration ? Math.round(Number(serviceQuote.data?.total || 0) * 100) : totalCents(proposal, tierId, upgrades);
  const products = requiredProducts(proposal, tierId);
  const choicesComplete = products.every((product) =>
    product.options.some(
      (option) => option.is_active && option.id === choices[String(product.id)],
    ),
  );
  const terminal =
    accepted || proposal.status === "accepted" || !!proposal.acceptance_date;
  const expired =
    !terminal &&
    (proposal.status === "expired" ||
      (proposal.valid_until && isCalendarDatePast(proposal.valid_until)));
  const signingRestriction = isPreview
    ? {
        title: "This is a preview",
        message: "You can review the proposal here. To sign, open the customer link after the proposal has been sent.",
        hint: "Preview only. Signing is disabled.",
      }
    : expired
      ? {
          title: "This proposal has expired",
          message: `Contact ${org.name} for an updated proposal before signing.`,
          hint: "This proposal has expired. Request an updated proposal.",
        }
      : proposal.status === "draft"
        ? {
            title: "This proposal is still a draft",
            message: `${org.name} has not sent this proposal for acceptance yet. You can review the details, but signing will be available after they send it.`,
            hint: "Draft proposal — not yet available for signing.",
          }
        : proposal.status === "declined"
          ? {
              title: "This proposal was declined",
              message: `Contact ${org.name} for an updated proposal before signing.`,
              hint: "This proposal was declined. Request an updated proposal.",
            }
          : !["sent", "viewed"].includes(proposal.status)
            ? {
                title: "Signing is unavailable",
                message: `Contact ${org.name} for a current proposal.`,
                hint: "This proposal is not available for signing.",
              }
            : null;
  // Keep the disabled button and its visible explanation in sync.
  const acceptanceHint = (() => {
    if (signingRestriction) return signingRestriction.hint;
    if (!packageReady) return configuration ? "Review all service choices and wait for your updated total before signing." : "Choose a package before signing.";
    if (!choicesComplete) return "Complete your product choices before signing.";
    if (name.trim().length < 2) return "Enter your full name to continue.";
    if (!signature.trim()) return "Draw or type your signature to continue.";
    if (!consent) return "Check the agreement box to enable signing.";
    return "";
  })();
  const canAccept = !terminal && !acceptanceHint;
  const logo = logoFor(brand, theme);
  const currentPhoto =
    photoIndex === null ? null : proposal.inspection_photos[photoIndex];
  const doc = proposal.presentation?.document;
  const documentQuery = useQuery({
    queryKey: ["proposal-edition", token, doc?.id],
    queryFn: async () =>
      (
        await api.get<ProposalDocumentStatus>(
          `/api/proposals/public/${token}/editions/${doc!.id}/`,
        )
      ).data,
    enabled: !!doc?.id && !isPreview,
    initialData: doc,
    refetchInterval: (query) =>
      ["pending", "processing"].includes(query.state.data?.status || "")
        ? 4000
        : false,
  });
  const accept = useMutation({
    mutationFn: () => {
      if (!canAccept)
        throw new Error(
          "Review your selections and complete the acceptance fields.",
        );
      return proposalsApi.accept(token, {
        tier_id: tierId,
        ...(configuration ? { service_selections: serviceSelections } : {}),
        name: name.trim(),
        signature,
        selected_upgrades: upgrades,
        product_selections: choices,
        comments,
        edition_id: proposal.presentation?.edition_id,
        source_version: proposal.presentation?.source_version,
        offer_version: proposal.project_offer?.version,
      });
    },
    onSuccess: () => {
      setAccepted(true);
      refresh();
    },
  });
  const chooseTier = (id: number) => {
    setTierId(id);
    const valid = requiredProducts(proposal, id);
    setChoices((previous) =>
      Object.fromEntries(
        Object.entries(previous).filter(([key, value]) =>
          valid.some(
            (product) =>
              String(product.id) === key &&
              product.options.some(
                (option) => option.is_active && option.id === value,
              ),
          ),
        ),
      ),
    );
    setConsent(false);
  };
  const go = (next: number) => {
    if (steps[next]) {
      setStepId(steps[next].id);
      window.scrollTo({ top: 0, behavior: "instant" });
      requestAnimationFrame(() => heading.current?.focus());
    }
  };
  const canContinue =
    step.type === "quote_details"
      ? packageReady
      : step.type === "choices"
        ? choicesComplete
        : true;
  const headingText =
    (
      {
        title: "A proposal for your property.",
        quote_details: "Choose the right system.",
        inspection: configuration && !configuration.include_roofing
          ? "What we found at your property."
          : "What we found on your roof.",
        measurements: configuration && !configuration.include_roofing
          ? "Measurements for your project."
          : "Your roof, measured.",
        choices: "Make it yours.",
        upgrades: "Required charges and optional upgrades.",
        investment: "Your project investment.",
        sign: signingRestriction ? "Review your proposal." : "Ready when you are.",
      } as Record<string, string>
    )[step.type] || step.title;
  const errorText = (() => {
    const error = accept.error as {
      responseData?: Record<string, unknown>;
      message?: string;
    } | null;
    if (!error) return "";
    return error.responseData
      ? Object.values(error.responseData).flat().map(String).join(" ")
      : error.message;
  })();
  const discount = tierDiscount(proposal, tier);
  const legacySummary = (
    <div className="px-summary">
      <div>
        <span>{discount ? "Package before discount" : "Selected package"}</span>
        <strong>{tier?.name || "Choose a package"}</strong>
        <b>{tier ? money(discount?.original ?? tier.total_amount) : "—"}</b>
      </div>
      {discount && (
        <div className="px-discount-row">
          <span>Package discount</span>
          <b>−{money(discount.savings)}</b>
        </div>
      )}
      {tier && (
        <div>
          <span>Included tax</span>
          <b>{money(tier.tax_amount)}</b>
        </div>
      )}
      {products.map((product) => (
        <div key={product.id}>
          <span>{product.name}</span>
          <strong>
            {product.options.find(
              (option) => option.id === choices[String(product.id)],
            )?.title || "Choose an option"}
          </strong>
        </div>
      ))}
      {proposal.optional_upgrades
        .filter((item) => item.is_required || upgrades.includes(item.id))
        .map((item) => (
          <div key={item.id}>
            <span>{item.name}{item.is_required ? " (Required)" : ""}</span>
            <b>{money(item.total_price)}</b>
          </div>
        ))}
      <div className="px-total">
        <span>Total project investment</span>
        <b>{tier ? money(total / 100) : "—"}</b>
      </div>
    </div>
  );
  const summary = configuration ? <div className="px-summary">
    {tier && <div><span>Roofing package</span><strong>{tier.name}</strong></div>}
    {priceReady && serviceQuote.data ? <ServicePriceSummary quote={serviceQuote.data} /> : <p role="status">{serviceQuote.isError ? 'Complete your service choices to calculate the total.' : 'Updating your project total…'}</p>}
    <div className="px-total"><span>Total project investment</span><b>{priceReady ? money(total / 100) : '—'}</b></div>
  </div> : legacySummary;
  const signingNotice = signingRestriction && (
    <div className="px-panel px-sign-unavailable" role="status">
      <p className="px-eyebrow">Review only</p>
      <h3>{signingRestriction.title}</h3>
      <p>{signingRestriction.message}</p>
    </div>
  );

  const content = (section: ProposalSection): ReactNode => {
    switch (section.type) {
      case "title":
        return (
          <>
            <div className="px-welcome">
              <div className={`px-hero ${coverImage ? "" : "px-hero-empty"}`}>
                {coverImage && (
                  <img
                    src={coverImage}
                    alt="Your property"
                    style={{
                      objectPosition: (options.cover_position || [50, 50])
                        .map((n) => `${n}%`)
                        .join(" "),
                    }}
                  />
                )}
                <div className="px-hero-content">
                  <p className="px-eyebrow">Prepared for</p>
                  <h2>{proposal.contact_name || "Your property"}</h2>
                  <p>{proposal.opportunity_address}</p>
                </div>
              </div>
              <div className="px-welcome-copy">
                <p className="px-eyebrow">{org.name}</p>
                <h2>{proposal.title}</h2>
                <p>
                  Explore the scope of work, compare your options, and choose
                  the details for your project.
                </p>
                {configuration && <div className="px-price-card"><span>Project total · current selections</span><strong>{priceReady ? money(total / 100) : 'Review your service choices'}</strong></div>}
                {!configuration && proposal.tiers.length > 0 && (
                  <div className="px-price-card">
                    <span>Your options range from</span>
                    <strong>
                      {money(
                        Math.min(
                          ...proposal.tiers.map((t) => packagePrice(proposal, t)),
                        ),
                      )}{" "}
                      –{" "}
                      {money(
                        Math.max(
                          ...proposal.tiers.map((t) => packagePrice(proposal, t)),
                        ),
                      )}
                    </strong>
                  </div>
                )}
              </div>
            </div>
            <div className="px-facts">
              <div>
                <span>Proposal</span>
                <strong>{proposal.proposal_number}</strong>
              </div>
              <div>
                <span>Prepared for</span>
                <strong>{proposal.contact_name}</strong>
              </div>
              <div>
                <span>Issued</span>
                <strong>{proposal.proposal_date}</strong>
              </div>
              <div>
                <span>Valid through</span>
                <strong>
                  {proposal.valid_until || "Contact your representative"}
                </strong>
              </div>
            </div>
            {!!options.credentials?.length && (
              <div className="px-credentials">
                {options.credentials.map((item, i) => (
                  <div key={i}>
                    <span className="px-eyebrow">{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
            )}
          </>
        );
      case "introduction":
        return (
          <div className="px-panel px-prose">
            <div className="px-pre">{proposal.introduction_text}</div>
            {!!proposal.scope_of_work.length && (
              <>
                <h3>Project scope</h3>
                <ul>
                  {proposal.scope_of_work.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        );
      case "inspection":
        return (
          <>
            <div className="px-photos">
              {proposal.inspection_photos.map((photo, i) => (
                <button
                  type="button"
                  className="px-photo"
                  key={photo.photo}
                  aria-label={`View inspection photo ${i + 1}`}
                  onClick={(event) => {
                    event.currentTarget.focus();
                    setPhotoIndex(i);
                  }}
                >
                  <AnnotatedPhoto
                    annotations={photo.annotations}
                    showAnnotations
                    src={photo.photo_url || photo.photo_thumbnail || undefined}
                    alt={photo.description || `Inspection photo ${i + 1}`}
                    loading="lazy"
                  />
                  <span>
                    <b>Photo {i + 1}</b>
                    <span>{photo.description}</span>
                    {photo.tags?.map((tag) => (
                      <small key={tag.id}>{tag.name}</small>
                    ))}
                  </span>
                </button>
              ))}
            </div>
            {!!options.findings?.length && (
              <div className="px-findings">
                <h3>Findings & impact</h3>
                {options.findings.map((finding, i) => (
                  <div key={i}>
                    <div>
                      <strong>{finding.title}</strong>
                      <small>{finding.location}</small>
                    </div>
                    <p>{finding.impact}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        );
      case "measurements":
        return <Measurements proposal={proposal} />;
      case "quote_details":
        return (
          <>
            {configuration && <ServiceChoices configuration={configuration} selections={serviceSelections} quote={serviceQuote.data} onChange={value => { setServiceSelections(value); setConsent(false); }} />}
            {configuration && serviceQuote.isError && <p role="alert">Choose an option for each required service. If pricing is unavailable, contact your representative.</p>}
            <div
              className="px-packages"
              role="radiogroup"
              aria-label="Choose a package"
            >
              {proposal.tiers.map((item) => (
                <label
                  className={`px-package ${item.is_recommended ? "px-recommended" : ""} ${tierId === item.id ? "px-selected" : ""}`}
                  key={item.id}
                >
                  <div className="px-package-head">
                    {hasDistinctTierLabel(item) && (
                      <p className="px-eyebrow">{item.tier_type_label}</p>
                    )}
                    <h3>{item.name}</h3>
                    <p>{item.description}</p>
                    {item.is_recommended && (
                      <span className="px-badge">Recommended</span>
                    )}
                  </div>
                  <div className="px-package-price">
                    <PackageDiscount proposal={proposal} tier={item} />
                    <strong>{money(packagePrice(proposal, item))}</strong>
                    <span>
                      {proposal.tax_enabled
                        ? `Includes ${money(item.tax_amount)} tax`
                        : "Package investment"}
                      {requiredFeeCents(proposal) > 0 && " · Required charges included"}
                    </span>
                  </div>
                  <ul>
                    {item.advantages.map((advantage, i) => (
                      <li key={i}>
                        <span aria-hidden="true">✓</span>
                        {advantage}
                      </li>
                    ))}
                  </ul>
                  {item.line_items.length > 0 && (
                    <div className="px-package-inclusions">
                      <p className="px-eyebrow" style={{ paddingInline: 22 }}>Included work</p>
                      <ul>{item.line_items.map((line) => <li key={line.id}><span aria-hidden="true">✓</span>{line.description}</li>)}</ul>
                    </div>
                  )}
                  <div className="px-pick">
                    <input
                      type="radio"
                      name="package"
                      value={item.id}
                      checked={tierId === item.id}
                      onChange={() => chooseTier(item.id)}
                    />
                    <span>
                      {tierId === item.id ? "Selected" : "Select this option"}
                    </span>
                  </div>
                </label>
              ))}
            </div>
            {proposal.tiers.length > 0 && <button
              className="px-button px-secondary"
              onClick={(event) => {
                event.currentTarget.focus();
                setComparison(true);
              }}
            >
              Compare options & full scope
            </button>}
          </>
        );
      case "choices":
        return (
          <>
            {products.map((product) => (
              <section className="px-product" key={product.id}>
                <h3>{product.name}</h3>
                <div
                  className="px-swatches"
                  role="radiogroup"
                  aria-label={`Choose an option for ${product.name}`}
                >
                  {product.options
                    .filter((option) => option.is_active)
                    .map((option) => (
                      <label
                        className={`px-swatch ${choices[String(product.id)] === option.id ? "px-selected" : ""}`}
                        key={option.id}
                      >
                        {(option.image_url || option.image) && (
                          <img
                            src={option.image_url || option.image!}
                            alt={option.title}
                            loading="lazy"
                          />
                        )}
                        <span>
                          <input
                            type="radio"
                            name={`product-${product.id}`}
                            checked={choices[String(product.id)] === option.id}
                            onChange={() => {
                              setChoices((previous) => ({
                                ...previous,
                                [product.id]: option.id,
                              }));
                              setConsent(false);
                            }}
                          />
                          <strong>{option.title}</strong>
                        </span>
                        {option.description && (
                          <small>{option.description}</small>
                        )}
                      </label>
                    ))}
                </div>
              </section>
            ))}
          </>
        );
      case "upgrades":
        return (
          <div className="px-upgrades">
            {[true, false].map(required => <section key={String(required)}>
            <h3>{required ? 'Required charges' : 'Optional upgrades'}</h3>
            {proposal.optional_upgrades.filter(item => Boolean(item.is_required) === required).map((item) => (
              <label
                key={item.id}
                className={item.is_required || upgrades.includes(item.id) ? "px-selected" : ""}
              >
                {item.is_required ? <span className="px-badge">Required</span> : <input
                  type="checkbox"
                  checked={upgrades.includes(item.id)}
                  onChange={() => {
                    setUpgrades((previous) =>
                      previous.includes(item.id)
                        ? previous.filter((id) => id !== item.id)
                        : [...previous, item.id],
                    );
                    setConsent(false);
                  }}
                />}
                <span>
                  <strong>{item.name}</strong>
                  <small>{item.description}</small>
                  <small>
                    {item.quantity} {item.unit} × {money(item.unit_price)}
                  </small>
                </span>
                <b>{money(item.total_price)}</b>
              </label>
            ))}
            </section>)}
          </div>
        );
      case "investment":
        return (
          <>
            {summary}
            {!!options.milestones?.length && (
              <>
                <h3 className="px-section-title">Your project, step by step</h3>
                <div className="px-timeline">
                  {paymentSchedule(options, total).map((item, i) => (
                    <div key={i}>
                      <span>{i + 1}</span>
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.description}</p>
                      </div>
                      {item.amount !== undefined && (
                        <b>
                          {priceReady ? money(item.amount / 100) : `${item.percent}%`}
                        </b>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
            {options.next_steps && (
              <div className="px-panel px-pre">{options.next_steps}</div>
            )}
            {proposal.financing_enabled &&
              !!proposal.financing_options.length && (
                <div className="px-panel">
                  <h3>Financing options</h3>
                  {proposal.financing_options.map((item, i) => (
                    <p key={i}>
                      {typeof item === "string"
                        ? item
                        : Object.entries(item as Record<string, unknown>)
                            .filter(
                              ([, value]) =>
                                typeof value === "string" ||
                                typeof value === "number",
                            )
                            .map(
                              ([key, value]) =>
                                `${key.replaceAll("_", " ")}: ${value}`,
                            )
                            .join(" · ")}
                    </p>
                  ))}
                </div>
              )}
          </>
        );
      case "terms":
        return (
          <div className="px-panel px-prose px-pre">
            {proposal.terms_and_conditions}
          </div>
        );
      case "sign":
        return (
          <div className="px-sign-layout">
            {signingRestriction && signingNotice}
            <div>
              {summary}
              {proposal.terms_and_conditions && (
                <details className="px-terms">
                  <summary>Read the complete terms</summary>
                  <div className="px-pre">{proposal.terms_and_conditions}</div>
                </details>
              )}
            </div>
            {!signingRestriction && (
              <div className="px-panel">
                <h3>Sign & accept</h3>
                <label className="px-field">
                  Your full name
                  <input
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    autoComplete="name"
                    maxLength={255}
                  />
                </label>
                <label className="px-field">
                  Comments <span className="px-muted">(optional)</span>
                  <textarea
                    value={comments}
                    onChange={(event) => setComments(event.target.value)}
                    rows={3}
                  />
                </label>
                <fieldset className="px-signature-mode">
                  <legend>Your signature</legend>
                  <label>
                    <input
                      type="radio"
                      name="signature-mode"
                      checked={signatureMode === "draw"}
                      onChange={() => {
                        setSignatureMode("draw");
                        setSignature("");
                      }}
                    />{" "}
                    Draw
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="signature-mode"
                      checked={signatureMode === "type"}
                      onChange={() => {
                        setSignatureMode("type");
                        setSignature("");
                      }}
                    />{" "}
                    Type
                  </label>
                </fieldset>
                {signatureMode === "draw" ? (
                  <Signature
                    key={proposal.presentation?.source_version}
                    value={signature}
                    onChange={setSignature}
                  />
                ) : (
                  <label className="px-field">
                    Type your signature
                    <input
                      value={signature}
                      onChange={(event) => setSignature(event.target.value)}
                      maxLength={255}
                    />
                  </label>
                )}
                <label className="px-consent">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(event) => setConsent(event.target.checked)}
                  />
                  <span>
                    I have reviewed the selected package, product choices,
                    upgrades and total, accept the terms, and authorize {org.name}{" "}
                    to carry out this work.
                  </span>
                </label>
                {!packageReady && (
                  <p className="px-error">Choose a package before accepting.</p>
                )}
                {!choicesComplete && (
                  <p className="px-error">
                    Complete the product choices before accepting.
                  </p>
                )}
                {errorText && (
                  <p role="alert" className="px-error">
                    {errorText}
                  </p>
                )}
              </div>
            )}
          </div>
        );
      case "custom":
        return (
          <div className="px-panel px-prose">
            {section.image && (
              <img
                className="px-diagram"
                src={section.image}
                alt={section.title}
              />
            )}
            {typeof section.content?.html === "string" ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(section.content.html, {
                    ALLOWED_TAGS: [
                      "p",
                      "div",
                      "span",
                      "br",
                      "h1",
                      "h2",
                      "h3",
                      "h4",
                      "strong",
                      "b",
                      "em",
                      "i",
                      "u",
                      "ul",
                      "ol",
                      "li",
                      "blockquote",
                      "table",
                      "thead",
                      "tbody",
                      "tr",
                      "th",
                      "td",
                      "a",
                    ],
                    ALLOWED_ATTR: ["href"],
                  }),
                }}
              />
            ) : (
              <div className="px-pre">
                {String(section.content?.text || section.content?.body || "")}
              </div>
            )}
          </div>
        );
      default:
        return section.asset ? (
          <div className="px-panel">
            <a
              className="px-resource"
              href={section.asset}
              target="_blank"
              rel="noreferrer"
            >
              Open {section.title} ↗
            </a>
          </div>
        ) : null;
    }
  };

  return (
    <div
      className="proposal-experience"
      data-proposal-theme={theme}
      style={themeStyle(brand, theme)}
    >
      {proposal.presentation?.font_css ? (
        <style>{proposal.presentation.font_css}</style>
      ) : (
        [brand.heading_font, brand.body_font].some(
          (font) => font !== "Arial",
        ) && (
          <link
            rel="stylesheet"
            href={`https://fonts.googleapis.com/css2?${[
              ...new Set([brand.heading_font, brand.body_font]),
            ]
              .filter((font) => font !== "Arial")
              .map(
                (font) =>
                  `family=${encodeURIComponent(font)}:wght@400;500;600;700;800;900`,
              )
              .join("&")}&display=swap`}
          />
        )
      )}
      <header className="px-topbar">
        <div className="px-topbar-inner">
          <div className={`px-logo ${logo.plate ? "px-logo-plate" : ""}`}>
            {logo.url ? (
              <img src={logo.url} alt={org.name} />
            ) : (
              <strong>{org.name}</strong>
            )}
          </div>
          <div className="px-meta">
            <b>{proposal.proposal_number}</b>
            <span>{proposal.contact_name}</span>
          </div>
          <div className="px-top-actions">
            {documentQuery.data?.pdf_url && !terminal && (
              <a
                className="px-button px-secondary"
                href={documentQuery.data.pdf_url}
              >
                Download PDF
              </a>
            )}
            <button
              className="px-icon"
              onClick={() => setTheme(theme === "light" ? "dark" : "light")}
              aria-label={`Use ${theme === "light" ? "dark" : "light"} theme`}
            >
              {theme === "light" ? "☾" : "☀"}
            </button>
          </div>
        </div>
        {!terminal && (
          <div className="px-progress">
            <div>
              <span>
                {isPreview
                  ? "Customer preview"
                  : proposal.status === "draft"
                    ? "Draft · review only"
                    : `Step ${index + 1} of ${steps.length}`}
              </span>
              <label htmlFor="proposal-section" className="px-sr">
                Jump to section
              </label>
              <select
                id="proposal-section"
                value={index}
                onChange={(event) => go(Number(event.target.value))}
              >
                {steps.map((item, i) => (
                  <option value={i} key={item.id}>
                    {item.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="px-progress-rail" aria-hidden="true">
              {steps.map((item, i) => (
                <i key={item.id} className={i <= index ? "px-done" : ""} />
              ))}
            </div>
          </div>
        )}
      </header>
      <main className="px-main">
        {!terminal && step.type !== "sign" && signingNotice}
        {terminal ? (
          <div className="px-confirmation">
            <div className="px-confirm-hero">
              <span aria-hidden="true">✓</span>
              <h1>Proposal accepted.</h1>
              <p>Your acceptance has been saved.</p>
            </div>
            <AcceptedSummary proposal={proposal} />
            {options.next_steps && (
              <div className="px-panel px-pre">
                <h3>What happens next</h3>
                {options.next_steps}
              </div>
            )}
            <p>
              Questions? Contact {org.name} at {org.phone}
              {org.email ? ` or ${org.email}` : ""}.
            </p>
          </div>
        ) : (
          <>
            <p className="px-eyebrow">{step.title}</p>
            <h1 ref={heading} tabIndex={-1}>
              {headingText}
            </h1>
            {step.intro && <p className="px-lede px-pre">{step.intro}</p>}
            {content(step)}
          </>
        )}
        <footer className="px-contact">
          {org.name}
          {org.phone && <a href={`tel:${org.phone}`}>{org.phone}</a>}
          {org.email && <a href={`mailto:${org.email}`}>{org.email}</a>}
        </footer>
      </main>
      {!terminal && (
        <nav className="px-actionbar" aria-label="Proposal navigation">
          <div>
            <button
              className="px-button px-secondary"
              disabled={index === 0}
              onClick={() => go(index - 1)}
            >
              Back
            </button>
            {priceReady && (
              <div className="px-running" aria-live="polite">
                <span>Your total</span>
                <strong>{money(total / 100)}</strong>
              </div>
            )}
            {step.type === "sign" ? (
              <button
                className="px-button px-primary"
                disabled={!canAccept || accept.isPending}
                aria-describedby={acceptanceHint ? "proposal-acceptance-hint" : undefined}
                onClick={() => accept.mutate()}
              >
                {accept.isPending
                  ? "Saving acceptance…"
                  : "Sign & accept proposal"}
              </button>
            ) : (
              <button
                className="px-button px-primary"
                disabled={!canContinue}
                onClick={() => go(index + 1)}
              >
                {step.type === "title" ? "Review your proposal" : "Continue"}{" "}
                <span aria-hidden="true">→</span>
              </button>
            )}
          </div>
          {step.type === "sign" && acceptanceHint ? (
            <small id="proposal-acceptance-hint" aria-live="polite">
              {acceptanceHint}
            </small>
          ) : !canContinue && (
            <small>
              {step.type === "choices"
                ? "Choose an option for each product to continue."
                : "Choose a package to continue."}
            </small>
          )}
        </nav>
      )}
      {comparison && (
        <Dialog
          title="Compare options & scope"
          onClose={() => setComparison(false)}
        >
          {!!options.comparison?.length && (
            <div className="px-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Features</th>
                    {proposal.tiers.map((item) => (
                      <th key={item.id}>
                        {hasDistinctTierLabel(item) && (
                          <>
                            {item.tier_type_label}
                            <br />
                          </>
                        )}
                        {item.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {options.comparison.map((row, i) => (
                    <tr key={i}>
                      <th>{row.label}</th>
                      {proposal.tiers.map((item) => (
                        <td key={item.id}>
                          {row[item.tier_type] || "Not specified"}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr>
                    <th>Investment</th>
                    {proposal.tiers.map((item) => (
                      <td key={item.id}>
                        <strong>{money(packagePrice(proposal, item))}</strong>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {!!proposal.scope_of_work.length && (
            <>
              <h3>Project scope</h3>
              <ul>
                {proposal.scope_of_work.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </>
          )}
          <div className="px-scope-grid">
            {proposal.tiers.map((item) => (
              <TierScope key={item.id} tier={item} />
            ))}
          </div>
        </Dialog>
      )}
      {currentPhoto && (
        <Dialog
          title={`Inspection photo ${photoIndex! + 1} of ${proposal.inspection_photos.length}`}
          onClose={() => setPhotoIndex(null)}
        >
          <AnnotatedPhoto
            annotations={currentPhoto.annotations}
            showAnnotations
            className="px-lightbox"
            src={currentPhoto.photo_url || undefined}
            alt={currentPhoto.description || "Inspection photograph"}
          />
          <p className="px-pre">{currentPhoto.description}</p>
          <div className="px-lightbox-nav">
            <button
              className="px-button px-secondary"
              disabled={photoIndex === 0}
              onClick={() => setPhotoIndex(photoIndex! - 1)}
            >
              Previous photo
            </button>
            <button
              className="px-button px-secondary"
              disabled={photoIndex === proposal.inspection_photos.length - 1}
              onClick={() => setPhotoIndex(photoIndex! + 1)}
            >
              Next photo
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function AcceptedSummary({ proposal }: { proposal: PublicProposal }) {
  const acceptance = proposal.acceptance_workflow;
  const order = acceptance?.order;
  return (
    <>
      {order && (
        <div className="px-summary">
          <div>
            <span>Selected system</span>
            <strong>{order.tier?.name || "Project services"}</strong>
          </div>
          {order.services?.map(service => <div key={service.key}><span>{service.name} · {service.option.name}</span><b>{money(service.total)}</b></div>)}
          {order.product_choices.map((item) => (
            <div key={item.id}>
              <span>Product selection</span>
              <strong>{item.label}</strong>
            </div>
          ))}
          {order.upgrades.map((item) => (
            <div key={item.id}>
              <span>{item.name}{item.is_required ? " (Required)" : ""}</span>
              <b>{money(item.total_price)}</b>
            </div>
          ))}
          <div className="px-total">
            <span>Accepted total</span>
            <b>{money(order.total)}</b>
          </div>
          {order.comments && <div className="px-pre">{order.comments}</div>}
        </div>
      )}
      {acceptance?.pdf_url ? (
        <a className="px-button px-primary" href={acceptance.pdf_url}>
          Download accepted paperwork
        </a>
      ) : (
        <p className="px-notice" role="status">
          {acceptance?.state === "error"
            ? "Your acceptance is saved. Paperwork is delayed; your representative can help recover it."
            : "Your acceptance is saved. Your paperwork is being prepared."}
        </p>
      )}
    </>
  );
}
